import React, { createContext, forwardRef, useContext, useMemo, useState } from "react";
import { cn } from "../../lib/utils";
import httpClient from "../../services/httpClient";

const AvatarContext = createContext(null);

export function getAvatarInitials(name = "") {
  const parts = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) return "US";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

const Avatar = forwardRef(function Avatar({ className = "", children, ...props }, ref) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const contextValue = useMemo(() => ({ imageLoaded, setImageLoaded }), [imageLoaded]);

  return (
    <AvatarContext.Provider value={contextValue}>
      <div
        ref={ref}
        className={cn(
          "relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-border bg-secondary text-xs font-semibold text-secondary-foreground",
          className
        )}
        {...props}
      >
        {children}
      </div>
    </AvatarContext.Provider>
  );
});

export const AvatarImage = forwardRef(function AvatarImage(
  { className = "", src, alt = "Avatar", onLoad, onError, ...props },
  ref
) {
  const context = useContext(AvatarContext);

  if (!src) {
    if (context?.imageLoaded) context.setImageLoaded(false);
    return null;
  }

  const resolvedSrc = resolveAvatarSrc(src);

  return (
    <img
      ref={ref}
      src={resolvedSrc}
      alt={alt}
      className={cn("h-full w-full rounded-[inherit] object-cover", className)}
      onLoad={(event) => {
        context?.setImageLoaded(true);
        onLoad?.(event);
      }}
      onError={(event) => {
        context?.setImageLoaded(false);
        onError?.(event);
      }}
      {...props}
    />
  );
});

function resolveAvatarSrc(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^data:/i.test(raw)) return raw;
  if (/^https?:\/\//i.test(raw)) return raw;

  const base = String(httpClient?.defaults?.baseURL || "").replace(/\/$/, "");
  if (!base) return raw;
  const suffix = raw.startsWith("/") ? raw : `/${raw}`;
  return `${base}${suffix}`;
}

export const AvatarFallback = forwardRef(function AvatarFallback(
  { className = "", children, ...props },
  ref
) {
  const context = useContext(AvatarContext);

  return (
    <span
      ref={ref}
      className={cn(
        "flex h-full w-full items-center justify-center rounded-[inherit] text-center",
        context?.imageLoaded && "hidden",
        className
      )}
      {...props}
    >
      {children}
    </span>
  );
});

const badgeToneClasses = {
  online: "bg-success",
  offline: "bg-destructive",
};

const badgeSizeClasses = {
  sm: "h-3.5 w-3.5 border-2",
  md: "h-4 w-4 border-2",
  lg: "h-5 w-5 border-[3px]",
};

export const AvatarBadge = forwardRef(function AvatarBadge(
  { className = "", tone = "online", size = "md", ...props },
  ref
) {
  return (
    <span
      ref={ref}
      className={cn(
        "absolute bottom-0 right-0 z-20 translate-x-[26%] translate-y-[26%] rounded-full border-background",
        badgeToneClasses[tone] || badgeToneClasses.online,
        badgeSizeClasses[size] || badgeSizeClasses.md,
        className
      )}
      {...props}
    />
  );
});

export default Avatar;
export { Avatar };
